import React, {useState} from 'react';
import {Box, IconButton} from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import {styled} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import ConfirmationModal from "../../../../shared/modals/ConfirmationModal";
import {AssetBoxContainer, AssetBoxWrapper, AssetLabel, UsedByBadge, UsedByChip} from "./AssetBoxStyle";
import GenericEditNameModal from "../modals/GenericEditNameModal";
import Asset from "../../../../../models/Asset";
import {UsedByEntry} from "../hooks/useUsedAssets";

const HoverControls = styled(Box)({
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px',
    justifyContent: 'space-between',
    width: '100%',
    background: 'linear-gradient(180deg, rgba(0,0,0,0.5), transparent)',
});

const DurationBadge = styled(Box)({
    position: 'absolute',
    bottom: 8,
    right: 8,
    background: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
});

// Helper function to format duration (e.g., 65 -> "1:05")
const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Asset Box Video Component
interface AssetBoxVideoProps {
    isSelected: boolean;
    asset: Asset;
    handleImageClick: (asset: Asset) => void;
    handleImageDoubleClick?: (asset: Asset) => void;
    handleImageDelete: (asset: Asset) => void;
    handleImageEdit: (asset: Asset) => void;
    usedByInputs?: UsedByEntry[];
}

export const AssetBoxVideo: React.FC<AssetBoxVideoProps> = ({
                                                                isSelected,
                                                                asset,
                                                                handleImageClick,
                                                                handleImageDoubleClick,
                                                                handleImageDelete,
                                                                handleImageEdit,
                                                                usedByInputs,
                                                            }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const {t} = useTranslation();

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const handleEditDialogOpen = () => setEditDialogOpen(true);
    const handleDeleteDialogOpen = () => setDeleteDialogOpen(true);
    const handleDeleteDialogClose = () => setDeleteDialogOpen(false);

    const confirmDelete = () => {
        handleImageDelete(asset);
        handleDeleteDialogClose();
    };

    return (
        <AssetBoxContainer
            item
            xs={4}
            md={3}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <AssetBoxWrapper
                onClick={() => handleImageClick(asset)}
                onDoubleClick={() => handleImageDoubleClick && handleImageDoubleClick(asset)}
                sx={{
                    "&::after": {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        left: 0,
                        border: isSelected ? '3px solid #2196f3' : '3px solid #ffffff78',
                        borderRadius: '16px',
                        pointerEvents: 'none'
                    }
                }}
            >
                <img
                    src={asset.thumbnail}
                    alt={asset.name}
                    loading="lazy"
                    width="100%"
                    height="100%"
                    style={{
                        display: 'block',
                        objectFit: 'cover',
                    }}
                />

                {/* Play icon overlay */}
                <PlayCircleIcon
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: 48,
                        color: 'white',
                        opacity: 0.9,
                        pointerEvents: 'none',
                    }}
                />

                {/* Duration badge */}
                {asset.duration && (
                    <DurationBadge>
                        {formatDuration(asset.duration)}
                    </DurationBadge>
                )}

                {isHovering && (
                    <HoverControls>
                        <IconButton
                            size="small"
                            sx={{color: 'white'}}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditDialogOpen();
                                setTimeout(() => setIsHovering(false), 100);
                            }}
                        >
                            <EditIcon fontSize="small"/>
                        </IconButton>
                        <IconButton
                            size="small"
                            sx={{color: 'white'}}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDialogOpen();
                                setTimeout(() => setIsHovering(false), 100);
                            }}
                        >
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    </HoverControls>
                )}

                {usedByInputs && usedByInputs.length > 0 && (
                    <UsedByBadge>
                        {usedByInputs.map((entry) => (
                            <UsedByChip key={entry.inputKey}>{entry.label}</UsedByChip>
                        ))}
                    </UsedByBadge>
                )}

            </AssetBoxWrapper>
            <AssetLabel>{asset.name}</AssetLabel>

            {isEditDialogOpen && <GenericEditNameModal<Asset>
                entity={asset}
                open={isEditDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onEntityEdited={handleImageEdit}
                apiEndpoint="/asset"
                dialogTitle="ערוך שם לסרטון"
                successMessage="הסרטון נערך בהצלחה"
                errorMessage="תקלה בעת עריכת הסרטון"
                placeholder="שם הסרטון"
                inputError="שם הסרטון נדרש"
            />}

            {isDeleteDialogOpen && <ConfirmationModal
                show={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                title="אישור מחיקה"
                message={`האם אתה בטוח שאתה רוצה למחוק את הסרטון "${asset.name}"?`}
            />}
        </AssetBoxContainer>
    );
};

// Memoized: the grid re-renders on every scroll/fetch page, but a tile only needs
// to update when its own asset/selection/usedBy props change.
export default React.memo(AssetBoxVideo);
